import {
  amountToDisplayValue,
  Attachment,
  Button,
  Color,
  Divider,
  Flex,
  IconButton,
  Modal,
  ModalProps,
  notify,
  Space,
  Spacing,
  Text,
  useModal,
} from '@billy/design-system'
import { format, Locale } from 'date-fns'
import { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useDeleteDuplicate } from '@features/attachments/hooks/useDeleteDuplicate'
import { useKeepDuplicate } from '@features/attachments/hooks/useKeepDuplicate'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { getParsedDataFromAttachment } from '@views/bills/elements/BillEditableForm/utils/getParsedDataFromAttachment'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useSegment } from '../../../../hooks'
import { useLocale } from '../../../../hooks/useLocale'
import { useOrgUsesReceiptsOnly } from '../../../../hooks/useOrgUsesReceiptsOnly'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { ReceiptPanel } from './elements/ReceiptPanel/ReceiptPanel'
import * as Styled from './styles'
import type { DuplicateReceipt } from './types'

type DuplicateParentFile = NonNullable<Attachment['duplicate']>

interface DuplicatePair {
  id: string
  incomingReceipt: DuplicateReceipt
  existingReceipt: DuplicateReceipt
}

type DuplicatedReceiptsModalProps = ModalProps & {
  duplicates: Attachment[]
}

type AttachmentWithDuplicate = Attachment & { duplicate: DuplicateParentFile }

const attachmentToReceipt = (attachment: Attachment, locale: Locale): DuplicateReceipt => {
  const parsed = getParsedDataFromAttachment(attachment.file)
  const documentDate = attachment.documentDate || attachment.createdTime

  return {
    imageUrl: attachment.file.downloadUrl || attachment.file.originalUrl,
    imageName: attachment.file.fileName || '',
    supplier: attachment.supplier || '',
    date: documentDate ? format(new Date(documentDate), 'P', { locale }) : '',
    dueDate: parsed?.dates.due ? format(new Date(parsed.dates.due), 'P', { locale }) : '',
    uploadDate: attachment.createdTime ? format(new Date(attachment.createdTime), 'P', { locale }) : '',
    description: attachment.comment || '',
    taxRate: '',
    inclVat: attachment.amount != null ? amountToDisplayValue(attachment.amount, false, attachment.currencyId) : '',
  }
}

const parentToReceipt = (
  duplicate: DuplicateParentFile,
  locale: Locale,
  organizationUrl?: string,
  currencyId?: string,
): DuplicateReceipt => {
  const parsed = getParsedDataFromAttachment(duplicate)
  const supplier = parsed?.vendor.name ?? ''
  const date = parsed?.dates.issued ? format(new Date(parsed.dates.issued), 'P', { locale }) : ''
  const dueDate = parsed?.dates.due ? format(new Date(parsed.dates.due), 'P', { locale }) : ''
  const uploadDate = duplicate.createdTime ? format(new Date(duplicate.createdTime), 'P', { locale }) : ''
  const inclVat =
    parsed?.amounts.totalInclVat != null
      ? amountToDisplayValue(parsed.amounts.totalInclVat, false, parsed.currency ?? currencyId)
      : ''
  let linkToFile: string | null = null
  if (duplicate.billId) {
    linkToFile = getOrganizationViewUrl(
      organizationUrl,
      reactRoute.organization.bills.getViewBillRoute(duplicate.billId),
    )
  } else if (duplicate.attachmentId) {
    linkToFile = getOrganizationViewUrl(
      organizationUrl,
      reactRoute.organization.bills.getNewBillFromAttachmentRoute(duplicate.attachmentId),
    )
  }
  return {
    imageUrl: duplicate.downloadUrl || duplicate.originalUrl,
    imageName: duplicate.fileName || '',
    supplier,
    date,
    dueDate,
    uploadDate,
    description: '',
    taxRate: '',
    inclVat,
    linkToFile,
  }
}

const isAttachmentWithDuplicate = (a: Attachment): a is AttachmentWithDuplicate => a.duplicate != null

const toPairs = (attachments: Attachment[], locale: Locale, organizationUrl?: string): DuplicatePair[] =>
  attachments.filter(isAttachmentWithDuplicate).map((a) => ({
    id: a.id,
    incomingReceipt: attachmentToReceipt(a, locale),
    existingReceipt: parentToReceipt(a.duplicate, locale, organizationUrl, a.currencyId),
  }))

export const DuplicatedReceiptsModal = ({
  id,
  duplicates,
  ...modalProps
}: DuplicatedReceiptsModalProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const locale = useLocale()
  const { organization } = useUserOrganization()
  const organizationUrl = organization?.url
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const { track } = useSegment()
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0)
  const [localPairs, setLocalPairs] = useState(() => toPairs(duplicates, locale, organizationUrl))
  useEffect(() => {
    setLocalPairs(toPairs(duplicates, locale, organizationUrl))
    setCurrentDuplicateIndex(0)
  }, [duplicates, locale, organizationUrl])
  const totalDuplicates = localPairs.length
  const currentPair = localPairs[currentDuplicateIndex] ?? {
    id: undefined,
    incomingReceipt: undefined,
    existingReceipt: undefined,
  }

  const { id: currentAttachmentId, incomingReceipt, existingReceipt } = currentPair
  const { close } = useModal(id)

  const advanceOrClose = (newPairs: typeof localPairs) => {
    if (newPairs.length === 0) {
      close()
    } else if (currentDuplicateIndex >= newPairs.length) {
      setCurrentDuplicateIndex(newPairs.length - 1)
    }
  }

  const { keepBoth, isLoading: isKeepingLoading } = useKeepDuplicate({
    onSuccess: () => {
      notify({
        id: NotificationKeys.DuplicateReceiptKeptBoth,
        message: t('receipts.duplicate_review_modal.notification.kept_both'),
        variant: 'success',
      })
      if (currentDuplicateIndex < totalDuplicates - 1) {
        setCurrentDuplicateIndex(currentDuplicateIndex + 1)
      } else {
        close()
      }
    },
  })

  const { deleteDuplicate, isLoading: isDeletingLoading } = useDeleteDuplicate({
    onSuccess: () => {
      notify({
        id: NotificationKeys.DuplicateReceiptDeleted,
        message: t('receipts.duplicate_review_modal.notification.deleted'),
        variant: 'success',
      })
      const newPairs = localPairs.filter((p) => p.id !== currentAttachmentId)
      setLocalPairs(newPairs)
      advanceOrClose(newPairs)
    },
  })
  const isLoading = isDeletingLoading || isKeepingLoading

  const handleDeleteDuplicateClick = () => {
    if (currentAttachmentId) {
      track(ExpensesTrackingEvents.DuplicateDeleteClicked)
      deleteDuplicate(currentAttachmentId)
    }
  }

  const handleKeepBothClick = () => {
    if (currentAttachmentId) {
      track(ExpensesTrackingEvents.DuplicateKeepBothClicked)
      keepBoth(currentAttachmentId)
    }
  }

  const handleOpenDocument = (url: string) => {
    track(ExpensesTrackingEvents.DuplicateOpenDocumentClicked)
    navigate(url)
  }

  return (
    <Modal id={id} size="xl" autofocusOnInput closable {...modalProps}>
      <Modal.Header title={t('receipts.duplicate_review_modal.title')}>
        {totalDuplicates > 1 && (
          <Styled.HeaderArrowWrapper>
            <Flex gap={Spacing.S2} alignItems="center">
              <IconButton
                color={Color.Black}
                icon="chevronUp"
                size="sm"
                variant="tonal"
                disabled={currentDuplicateIndex <= 0}
                onClick={() => setCurrentDuplicateIndex(currentDuplicateIndex - 1)}
              />
              <IconButton
                color={Color.Black}
                icon="chevronDown"
                size="sm"
                variant="tonal"
                disabled={currentDuplicateIndex >= totalDuplicates - 1}
                onClick={() => setCurrentDuplicateIndex(currentDuplicateIndex + 1)}
              />
              <Text>
                {currentDuplicateIndex + 1}/{totalDuplicates}
              </Text>
            </Flex>
          </Styled.HeaderArrowWrapper>
        )}
      </Modal.Header>
      <Modal.Body>
        <Styled.ReceiptPanelsWrapper>
          <Text>{t('receipts.duplicate_review_modal.description')}</Text>
          <Space size="s24" />
          {incomingReceipt && existingReceipt && (
            <Flex gap={Spacing.S4} alignItems="stretch">
              <ReceiptPanel receipt={incomingReceipt} isIncoming />
              <Styled.DividerWrapper>
                <Divider direction="vertical" />
              </Styled.DividerWrapper>
              <ReceiptPanel
                receipt={existingReceipt}
                isIncoming={false}
                onOpenDocument={orgUsesReceiptsOnly ? undefined : handleOpenDocument}
              />
            </Flex>
          )}
        </Styled.ReceiptPanelsWrapper>
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyContent="center" gap={Spacing.S4}>
          <Button
            variant="tonal"
            color="danger"
            loading={isLoading}
            disabled={!currentAttachmentId || isLoading}
            onClick={handleDeleteDuplicateClick}
          >
            {t('receipts.duplicate_review_modal.delete_duplicate')}
          </Button>
          <Button
            variant="filled"
            loading={isLoading}
            disabled={!currentAttachmentId || isLoading}
            onClick={handleKeepBothClick}
          >
            {t('receipts.duplicate_review_modal.keep_both')}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}
